import { useCallback, useRef, useState } from 'react'
import { generateUuid } from '../../../../utils/uuids'
import {
  AiAgentChatDraft,
  AiAgentQueuedMessage,
  mergeDrafts,
} from '../queuedMessages'
import { releaseImageAttachments } from './useAiAgentImageUpload'

const EMPTY_DRAFT: AiAgentChatDraft = { text: '', images: [], wbsItems: [] }

/**
 * AI応答中に送ったメッセージを溜めておく送信待ち行列を管理するフック。
 *
 * 行列の項目は編集できる。編集中はその項目を行列に残したまま（一覧では「編集中」として見せる）
 * 内容を入力欄へ移し、入力欄にあった下書きはこのフックが預かって編集の確定・取消で戻す。
 *
 * 行列と編集状態は ref を正として持つ。ターン終了直後の自動送信のように、更新と参照が
 * レンダーを挟まずに続けて起きる場面があり、state のクロージャでは取りこぼすため。
 */
export const useAiAgentMessageQueue = () => {
  const [queuedMessages, setQueuedMessages] = useState<AiAgentQueuedMessage[]>(
    []
  )
  const [editingMessageId, setEditingMessageId] = useState<string | undefined>(
    undefined
  )
  const queuedMessagesRef = useRef<AiAgentQueuedMessage[]>([])
  const editingMessageIdRef = useRef<string | undefined>(undefined)
  // 編集開始時に入力欄にあった下書き。編集の確定・取消で入力欄へ戻す。
  const stashedDraftRef = useRef<AiAgentChatDraft>(EMPTY_DRAFT)
  // 編集開始時の本文。取消でここへ戻す。
  const originalTextRef = useRef('')

  const updateQueue = useCallback(
    (updater: (prev: AiAgentQueuedMessage[]) => AiAgentQueuedMessage[]) => {
      const next = updater(queuedMessagesRef.current)
      queuedMessagesRef.current = next
      setQueuedMessages(next)
    },
    []
  )

  const updateEditingMessageId = useCallback((id: string | undefined) => {
    editingMessageIdRef.current = id
    setEditingMessageId(id)
  }, [])

  const enqueue = useCallback(
    (draft: AiAgentChatDraft) => {
      updateQueue(prev => [...prev, { ...draft, id: generateUuid() }])
    },
    [updateQueue]
  )

  const removeQueuedMessage = useCallback(
    (id: string) => {
      updateQueue(prev => {
        const target = prev.find(message => message.id === id)
        if (target) {
          releaseImageAttachments(target.images)
        }
        return prev.filter(message => message.id !== id)
      })
    },
    [updateQueue]
  )

  /**
   * 指定した項目の編集を始める。入力欄の下書きを預かり、代わりに入力欄へ入れる内容を返す。
   * 別の項目を編集中の場合は何もしない（undefined を返す）。
   */
  const startEditing = useCallback(
    (
      id: string,
      currentDraft: AiAgentChatDraft
    ): AiAgentChatDraft | undefined => {
      if (editingMessageIdRef.current) {
        return undefined
      }
      const target = queuedMessagesRef.current.find(
        message => message.id === id
      )
      if (!target) {
        return undefined
      }
      stashedDraftRef.current = currentDraft
      originalTextRef.current = target.text
      updateEditingMessageId(id)
      return {
        text: target.text,
        images: target.images,
        wbsItems: target.wbsItems,
      }
    },
    [updateEditingMessageId]
  )

  /**
   * 編集を確定して入力欄の内容を項目へ書き戻し、預かっていた下書きを返す。
   * 本文が空になった場合はその項目を送る意味がないため、行列から取り除く。
   */
  const commitEditing = useCallback(
    (draft: AiAgentChatDraft): AiAgentChatDraft => {
      const id = editingMessageIdRef.current
      if (!id) {
        return EMPTY_DRAFT
      }
      const text = draft.text.trim()
      if (text === '') {
        releaseImageAttachments(draft.images)
        updateQueue(prev => prev.filter(message => message.id !== id))
      } else {
        updateQueue(prev =>
          prev.map(message =>
            message.id === id ? { ...draft, text, id } : message
          )
        )
      }
      updateEditingMessageId(undefined)
      return stashedDraftRef.current
    },
    [updateQueue, updateEditingMessageId]
  )

  /**
   * 編集を取り消して本文を編集前へ戻し、預かっていた下書きを返す。
   * 添付は入力欄で外した時点でプレビューを解放しており元に戻せないため、
   * 本文と違って編集中の操作結果をそのまま項目へ書き戻す。
   */
  const cancelEditing = useCallback(
    (draft: AiAgentChatDraft): AiAgentChatDraft => {
      const id = editingMessageIdRef.current
      if (!id) {
        return EMPTY_DRAFT
      }
      updateQueue(prev =>
        prev.map(message =>
          message.id === id
            ? {
                ...message,
                text: originalTextRef.current,
                images: draft.images,
                wbsItems: draft.wbsItems,
              }
            : message
        )
      )
      updateEditingMessageId(undefined)
      return stashedDraftRef.current
    },
    [updateQueue, updateEditingMessageId]
  )

  /**
   * 送信できる項目をまとめて取り出し、行列から取り除く。
   * 編集中は何も取り出さない。編集中の項目だけ後回しにすると送信順が入れ替わるため、
   * 行列全体を編集の確定・取消まで待たせる。
   */
  const takeQueuedMessagesForSend = useCallback(():
    | AiAgentChatDraft
    | undefined => {
    if (editingMessageIdRef.current) {
      return undefined
    }
    const sendable = queuedMessagesRef.current
    if (sendable.length === 0) {
      return undefined
    }
    updateQueue(() => [])
    return mergeDrafts(sendable)
  }, [updateQueue])

  /** 行列を破棄する。破棄した件数を返す（呼び出し側が通知の要否を判断する）。 */
  const discardQueue = useCallback((): number => {
    const editingId = editingMessageIdRef.current
    const discarded = queuedMessagesRef.current
    discarded
      // 編集中の項目の添付は入力欄に表示されたままなので解放しない（プレビューが壊れる）。
      .filter(message => message.id !== editingId)
      .forEach(message => releaseImageAttachments(message.images))
    updateQueue(() => [])
    updateEditingMessageId(undefined)
    stashedDraftRef.current = EMPTY_DRAFT
    return discarded.length
  }, [updateQueue, updateEditingMessageId])

  return {
    queuedMessages,
    editingMessageId,
    enqueue,
    removeQueuedMessage,
    startEditing,
    commitEditing,
    cancelEditing,
    takeQueuedMessagesForSend,
    discardQueue,
  }
}
